import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import config from '../../../config/environment';
import banner from '../../../assets/banner/tourBan.webp';

const NewsletterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SignUpCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2.5rem;
  opacity: 0.8;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const Input = styled.input`
  padding: 1rem 1.25rem;
  border: 2px solid ${props => props.error ? '#dc3545' : props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  font-family: ${props => props.theme.fonts.main};
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#dc3545' : props.theme.colors.accent};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(220, 53, 69, 0.1)' : 'rgba(157, 109, 73, 0.1)'};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const TagsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TagsLabel = styled(Label)`
  margin-bottom: 0.5rem;
`;

const TagsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TagButton = styled.button`
  padding: 1rem 1.5rem;
  border: 2px solid ${props => props.selected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 8px;
  background: ${props => props.selected 
    ? `linear-gradient(135deg, ${props.theme.colors.accent}, #b87d5a)` 
    : 'white'};
  color: ${props => props.selected ? 'white' : props.theme.colors.primary};
  font-size: 1rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  text-align: center;
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  ${props => props.selected && `
    box-shadow: 0 4px 12px rgba(157, 109, 73, 0.3);
  `}
`;

const TagCheckmark = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: white;
  font-size: 0.9rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, #b87d5a);
  color: white;
  border: none;
  padding: 1.25rem 2.5rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.5px;
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(157, 109, 73, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  margin-top: 1rem;
  font-family: ${props => props.theme.fonts.main};
  
  ${props => props.error ? `
    background: #ffe6e6;
    color: #dc3545;
    border: 1px solid #f5c6cb;
  ` : `
    background: #e6f7e6;
    color: #2d7a2d;
    border: 1px solid #c3e6c3;
  `}
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.greyBackground}, #f8f8f8);
  padding: 1.5rem;
  border-radius: 12px;
  margin-top: 2rem;
  border-left: 4px solid ${props => props.theme.colors.accent};
  
  p {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1rem;
    color: ${props => props.theme.colors.primary};
    line-height: 1.6;
    margin: 0;
  }
`;

const parseResponse = async (response) => {
  if (!response) {
    return { data: null, message: null };
  }

  try {
    const json = await response.clone().json();
    return {
      data: json,
      message: typeof json?.message === 'string' ? json.message : null
    };
  } catch {
    try {
      const text = await response.clone().text();
      return {
        data: null,
        message: text || null
      };
    } catch {
      return { data: null, message: null };
    }
  }
};

const NewsletterSignUp = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  
  const [selectedTags, setSelectedTags] = useState([]);
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    status: null
  });

  // Tag configuration: display name -> Mailchimp tag value
  const availableTags = [
    { display: 'Castle', value: 'Castle' },
    { display: 'Farm Shop', value: 'farmshop' },
    { display: 'Duchess Collection', value: 'Duchess Collection' },
    { display: 'Retail Village', value: 'Retail Village' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (status.error) {
      setStatus(prev => ({ ...prev, error: null }));
    }
  };

  const handleTagToggle = (tagValue) => {
    setSelectedTags(prev => {
      if (prev.includes(tagValue)) {
        return prev.filter(tag => tag !== tagValue);
      } else {
        return [...prev, tagValue];
      }
    });
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      return 'Please enter your first name';
    }
    if (!formData.lastName.trim()) {
      return 'Please enter your last name';
    }
    if (!formData.email.trim()) {
      return 'Please enter your email address';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    if (selectedTags.length === 0) {
      return 'Please select at least one newsletter category';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setStatus({
        submitting: false,
        error: validationError,
        success: false,
        status: null
      });
      return;
    }

    setStatus({ submitting: true, error: null, success: false, status: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': config.frontendUrl
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({
          ...formData,
          origin: window.location.origin,
          tags: selectedTags
        })
      });
      
      const { data, message } = await parseResponse(response);

      if (!response.ok || data?.success === false) {
        throw new Error(
          (data && typeof data.message === 'string' && data.message) ||
          message ||
          `Subscription failed${response.status ? ` (${response.status})` : ''}`
        );
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        status: data?.status ?? null
      });
      
      if (data?.status === 'new_subscription') {
        setFormData({ firstName: '', lastName: '', email: '' });
        setSelectedTags([]);
      }
    } catch (error) {
      setStatus({
        submitting: false,
        error: error.message || 'An error occurred while subscribing. Please try again.',
        success: false,
        status: null
      });
    }
  };

  const getSuccessMessage = () => {
    if (status.status === 'already_subscribed') {
      return 'You are already subscribed to our newsletter!';
    } else if (status.status === 'pending_confirmation') {
      return 'Thank you for subscribing! Please check your email to confirm your subscription.';
    } else if (status.status === 'new_subscription') {
      return 'Thank you for subscribing! You have been successfully added to our newsletter.';
    }
    return 'Thank you for subscribing!';
  };

  return (
    <NewsletterContainer>
      <ParallaxBanner 
        title="Newsletter Sign Up"
        subtitle="Stay Connected with Belvoir Castle"
        image={banner}
      />
      
      <ContentContainer>
        <SignUpCard>
          <Title>Stay in the Loop</Title>
          <Subtitle>
            Subscribe to our newsletter and be the first to hear about upcoming events, 
            special offers, and news from Belvoir Castle.
          </Subtitle>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={handleInputChange}
                error={status.error && !formData.firstName.trim()}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={handleInputChange}
                error={status.error && !formData.lastName.trim()}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleInputChange}
                error={status.error && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))}
              />
            </FormGroup>

            <TagsSection>
              <TagsLabel>Select Newsletter Categories *</TagsLabel>
              <TagsContainer>
                {availableTags.map(tag => (
                  <TagButton
                    key={tag.value}
                    type="button"
                    selected={selectedTags.includes(tag.value)}
                    onClick={() => handleTagToggle(tag.value)}
                  >
                    {tag.display}
                    {selectedTags.includes(tag.value) && (
                      <TagCheckmark>
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </TagCheckmark>
                    )}
                  </TagButton>
                ))}
              </TagsContainer>
            </TagsSection>

            <SubmitButton 
              type="submit" 
              disabled={status.submitting}
            >
              {status.submitting ? (
                'SUBSCRIBING...'
              ) : (
                <>
                  <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.5rem' }} />
                  SUBSCRIBE NOW
                </>
              )}
            </SubmitButton>

            {status.error && (
              <StatusMessage error>
                <FontAwesomeIcon icon={faExclamationCircle} />
                {status.error}
              </StatusMessage>
            )}

            {status.success && (
              <StatusMessage>
                <FontAwesomeIcon icon={faCheckCircle} />
                {getSuccessMessage()}
              </StatusMessage>
            )}
          </Form>

          <InfoBox>
            <p>
              <strong>What to expect:</strong> By subscribing, you'll receive regular updates about events, 
              special offers, and news from the areas you've selected. You can unsubscribe at any time by 
              clicking the link in our emails.
            </p>
          </InfoBox>
        </SignUpCard>
      </ContentContainer>
      
      <Footer />
    </NewsletterContainer>
  );
};

export default NewsletterSignUp;

